import { makeAutoObservable, runInAction } from 'mobx';
import { apiClient } from '../api/apiclient';
import { academyStore } from './AcademyStore';
export class SessionStore {
    constructor() {
        this.templates = [];
        this.sessions = [];
        this.loading = false;
        this.error = null;
        this.selectedTemplate = null;
        this.selectedSession = null;
        this.isTemplateDialogOpen = false;
        this.isSessionDialogOpen = false;
        this.isAttendanceDialogOpen = false;
        this.isViewDialogOpen = false;
        this.viewDialogType = null;
        this.viewTemplate = null;
        this.viewSession = null;
        this.isEditingTemplate = false;
        makeAutoObservable(this);
    }
    // Template actions
    async fetchTemplates(academyId, filters) {
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.getSessionTemplatesByAcademyId(academyId, filters);
            if (response.success) {
                runInAction(() => {
                    this.templates = response.data;
                    this.loading = false;
                });
            }
            else {
                throw new Error(response.message);
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to fetch session templates';
                this.loading = false;
            });
        }
    }
    async createTemplate(templateData) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.createSessionTemplate(academyStore.currentAcademy.id, templateData);
            if (response.success) {
                runInAction(() => {
                    this.templates.unshift(response.data);
                    this.loading = false;
                });
                return response.data;
            }
            else {
                throw new Error(response.message);
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to create session template';
                this.loading = false;
            });
            throw error;
        }
    }
    async updateTemplate(id, templateData) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.updateSessionTemplate(academyStore.currentAcademy.id, id, templateData);
            if (response.success) {
                runInAction(() => {
                    const index = this.templates.findIndex(t => t.id === id);
                    if (index !== -1) {
                        this.templates[index] = response.data;
                    }
                    this.loading = false;
                });
                return response.data;
            }
            else {
                throw new Error(response.message);
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to update session template';
                this.loading = false;
            });
            throw error;
        }
    }
    async deleteTemplate(id) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.deleteSessionTemplate(academyStore.currentAcademy.id, id);
            if (response.success) {
                runInAction(() => {
                    this.templates = this.templates.filter(t => t.id !== id);
                    this.loading = false;
                });
            }
            else {
                throw new Error(response.message);
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to delete session template';
                this.loading = false;
            });
            throw error;
        }
    }
    // Session actions
    async fetchSessions(academyId, filters) {
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.getSessionsByAcademyId(academyId, filters);
            if (response.success) {
                runInAction(() => {
                    this.sessions = response.data;
                    this.loading = false;
                });
            }
            else {
                throw new Error(response.message);
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to fetch sessions';
                this.loading = false;
            });
        }
    }
    async createSession(sessionData) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.createSession(academyStore.currentAcademy.id, sessionData);
            if (response.success) {
                runInAction(() => {
                    this.sessions.unshift(response.data);
                    this.loading = false;
                });
                return response.data;
            }
            else {
                throw new Error(response.message);
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to create session';
                this.loading = false;
            });
            throw error;
        }
    }
    async updateSession(id, sessionData) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.updateSession(academyStore.currentAcademy.id, id, sessionData);
            if (response.success) {
                runInAction(() => {
                    const index = this.sessions.findIndex(s => s.id === id);
                    if (index !== -1) {
                        this.sessions[index] = response.data;
                    }
                    this.loading = false;
                });
                return response.data;
            }
            else {
                throw new Error(response.message);
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to update session';
                this.loading = false;
            });
            throw error;
        }
    }
    async deleteSession(id) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.deleteSession(academyStore.currentAcademy.id, id);
            if (response.success) {
                runInAction(() => {
                    this.sessions = this.sessions.filter(s => s.id !== id);
                    this.loading = false;
                });
            }
            else {
                throw new Error(response.message);
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to delete session';
                this.loading = false;
            });
            throw error;
        }
    }
    async markAttendance(sessionId, userId, attended, markedBy) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.markAttendance(academyStore.currentAcademy.id, sessionId, userId, attended, markedBy);
            if (response.success) {
                runInAction(() => {
                    const index = this.sessions.findIndex(s => s.id === sessionId);
                    if (index !== -1) {
                        this.sessions[index] = response.data;
                    }
                    this.loading = false;
                });
                return response.data;
            }
            else {
                throw new Error(response.message);
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to mark attendance';
                this.loading = false;
            });
            throw error;
        }
    }
    async updateAttendanceBulk(sessionId, attendanceUpdates, markedBy) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.updateAttendanceBulk(academyStore.currentAcademy.id, sessionId, attendanceUpdates, markedBy);
            if (response.success) {
                runInAction(() => {
                    const index = this.sessions.findIndex(s => s.id === sessionId);
                    if (index !== -1) {
                        this.sessions[index] = response.data;
                    }
                    this.loading = false;
                });
                return response.data;
            }
            else {
                throw new Error(response.message);
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to update attendance';
                this.loading = false;
            });
            throw error;
        }
    }
    // Dialog actions
    openCreateTemplateDialog() {
        this.selectedTemplate = null;
        this.isEditingTemplate = false;
        this.isTemplateDialogOpen = true;
    }
    openEditTemplateDialog(template) {
        this.selectedTemplate = template;
        this.isEditingTemplate = true;
        this.isTemplateDialogOpen = true;
    }
    closeTemplateDialog() {
        this.isTemplateDialogOpen = false;
        this.selectedTemplate = null;
        this.isEditingTemplate = false;
    }
    openCreateSessionDialog() {
        this.selectedSession = null;
        this.isSessionDialogOpen = true;
    }
    closeSessionDialog() {
        this.isSessionDialogOpen = false;
        this.selectedSession = null;
    }
    openAttendanceDialog(session) {
        this.selectedSession = session;
        this.isAttendanceDialogOpen = true;
    }
    closeAttendanceDialog() {
        this.isAttendanceDialogOpen = false;
        this.selectedSession = null;
    }
    openViewTemplateDialog(template) {
        this.viewTemplate = template;
        this.viewSession = null;
        this.viewDialogType = 'template';
        this.isViewDialogOpen = true;
    }
    openViewSessionDialog(session) {
        this.viewSession = session;
        this.viewTemplate = null;
        this.viewDialogType = 'session';
        this.isViewDialogOpen = true;
    }
    closeViewDialog() {
        this.isViewDialogOpen = false;
        this.viewDialogType = null;
        this.viewTemplate = null;
        this.viewSession = null;
    }
    // Computed
    get hasTemplates() {
        return this.templates.length > 0;
    }
    get hasSessions() {
        return this.sessions.length > 0;
    }
    get isLoading() {
        return this.loading;
    }
    get hasError() {
        return this.error !== null;
    }
    // Helper methods to get upcoming and past sessions
    getUpcomingSessions(days = 7) {
        const now = new Date();
        const future = new Date();
        future.setDate(now.getDate() + days);
        return this.sessions
            .filter(session => {
            const sessionDate = new Date(session.schedule);
            return sessionDate >= now && sessionDate <= future;
        })
            .sort((a, b) => new Date(a.schedule).getTime() - new Date(b.schedule).getTime());
    }
    getPastSessions(days = 7) {
        const now = new Date();
        const past = new Date();
        past.setDate(now.getDate() - days);
        return this.sessions
            .filter(session => {
            const sessionDate = new Date(session.schedule);
            return sessionDate < now && sessionDate >= past;
        })
            .sort((a, b) => new Date(b.schedule).getTime() - new Date(a.schedule).getTime());
    }
}
export const sessionStore = new SessionStore();
